import React, { useEffect, useLayoutEffect, useRef } from "react";
import type { GameState, Player } from "../shared/types";

import forEach from "core/src/core/util/for-each.js";

import requestFrame from "../shared/request-frame.js";
import MapRenderer from "./renderers/map-renderer.js";
import WormHeadRenderer from "./renderers/worm-head-renderer.js";
import PowerUpRenderer from "./renderers/power-up-renderer.js";
import WormBodyRenderer from "./renderers/worm-body-renderer.js";
import PredictedWormRenderer from "./renderers/predicted-worm-renderer.js";
import ServerGhostRenderer from "./renderers/server-ghost-renderer.js";
import SpotlightRenderer from "./renderers/spotlight-renderer";
import SwitchAnimRenderer from "./renderers/switch-anim-renderer.js";
import { createOpponentInterpolator } from "../online/interpolation";
import { recordContextLost, recordContextRestored } from "../shared/canvas-diagnostics";
import * as csf from "./canvas-state-functions.js";
import { useArenaView } from "./arena-view";
import { ensureColors } from "../shared/worm-colors";

import "./game-canvas.scss";

// Who is who on the board, as the renderers draw it: ids, colours, heads, flags,
// and which seats are ours.
function rosterSig(players: any[] | undefined, localIds: any[] | undefined): string {
  let s = (localIds || []).join(",") + "|";
  for (const pl of players || []) s += pl.id + ":" + pl.colorId + ":" + (pl.headStyle || "") + ":" + (pl.flag || "") + ";";
  return s;
}

interface Props {
  gameState: GameState;
  players: Player[];
  config?: any;
  renderTime?: number | (() => number);
  // Client-side prediction for the local player's own worm(s) (online play only).
  // One or more local players can be owned (two-on-one-device online); `predictors`
  // maps each owned playerId to its controller.
  localPlayerIds?: any[];
  predictors?: Record<string, any>;
  getTargetTime?: () => number;
  // Debug: tint the predicted (own-worm) layer red so it can be told apart from
  // the shared body layer when chasing rendering artifacts.
  debugPredicted?: boolean;
  // Online: render each opponent at its own MEASURED delay (per-track jitter
  // cushion; see interpolation.ts). adaptMarginSec is the minimum cushion (floor).
  adaptiveDelay?: boolean;
  adaptMarginSec?: number;
  // Estimated current server time (no render delay); the adaptive clocks measure
  // each track's data age against it. Falls back to powerUpTruthTime, then the
  // (delayed) render clock.
  serverTime?: () => number;
  // Live handle for the netstats overlay: filled with the interpolator's getStats.
  interpStatsRef?: { current: ((serverNow: number) => any) | null };
  // Debug (`?ghost=1`): overlay a ring where the SERVER has each worm's head at
  // server-now (no render delay), to see the gap vs the delayed/predicted view.
  serverGhost?: { getSegment: (playerId: any) => any; getTime: () => number };
  // Online: the "truth" clock (server-now) at which power-ups should APPEAR, so a
  // static power-up isn't hidden behind the opponent render delay. Despawns still
  // use the render clock. Omitted offline (power-ups spawn on the render clock).
  powerUpTruthTime?: () => number;
  // Round handover: fade the board's contents (power-ups, trails, heads, FX) out
  // over the end of the round-over wait, so the next round starts on a clean
  // board instead of the old trails cutting to the new spawns. `at` is seconds
  // after this prop is first applied, `duration` how long the fade takes; the
  // map and its wall stay. `paused` holds it (a dialog holding the countdown).
  contentFade?: { at: number; duration: number; paused?: boolean };
  children?: React.ReactNode;
}

export default function GameCanvas(props: Props) {
  const { config = {}, children, contentFade } = props;
  // Turned a quarter on an upright phone (arena-view.ts): the layers keep their
  // landscape backing stores and are turned in CSS, so every renderer draws
  // exactly as before; only the upright sprites are counter-turned.
  const { rotated } = useArenaView();

  // The render loop reads the latest props through a ref.
  const propsRef = useRef(props);
  propsRef.current = props;
  const rotatedRef = useRef(rotated);
  rotatedRef.current = rotated;

  const containerRef = useRef<HTMLDivElement>(null);

  // Stacked <canvas> layers, keyed by name (mirrors the old string refs).
  const canvasRefs = useRef<Record<string, HTMLCanvasElement>>({});
  const setCanvasRef = (name: string) => (el: HTMLCanvasElement | null) => {
    if (el) canvasRefs.current[name] = el;
  };

  // Mutable per-frame render bookkeeping (renderers, requestId, path progress).
  const canvasStateRef = useRef<any>(null);
  if (!canvasStateRef.current) canvasStateRef.current = csf.createState();

  // Per-opponent interpolation clocks (online adaptive delay).
  const interpRef = useRef<ReturnType<typeof createOpponentInterpolator> | null>(null);
  // The roster the renderers were last built with (see renderFrame).
  const rosterRef = useRef<{ players: any; localIds: any; sig: string | null }>({ players: null, localIds: null, sig: null });
  if (!interpRef.current) interpRef.current = createOpponentInterpolator();

  // The layers are sized in CSS pixels, so on a high-density screen (a phone,
  // 3 device px per CSS px) the browser stretches them. Solid trails survive
  // that; the power-up tokens do not (a small glyph on a big map went to
  // mush). So the power-up layer alone gets the screen's density, capped at 2x
  // (it redraws only the tokens' boxes, so the extra pixels are cheap there,
  // unlike the full-canvas layers).
  function resizeCanvases() {
    const dense = Math.min(2, Math.max(1, (typeof window !== "undefined" && window.devicePixelRatio) || 1));
    forEach(canvasRefs.current, (canvas: HTMLCanvasElement) => {
      const k = canvas === canvasRefs.current.powerUpCanvas ? dense : 1;
      canvas.width = Math.round(canvas.offsetWidth * k);
      canvas.height = Math.round(canvas.offsetHeight * k);
    });
  }

  function setupRenderers() {
    const refs = canvasRefs.current;
    const canvasState = canvasStateRef.current;
    const p = propsRef.current;
    ensureColors(p.players);
    const cfg = p.config || {};
    const scale = refs.mapCanvas.width / p.gameState.map.width;
    const rotated = rotatedRef.current;
    const renderers = canvasState.renderers;
    renderers.length = 0;
    // Online prediction: the local worm(s) this client owns. A power-up is hidden
    // the instant ANY local worm (predictively) drives over it.
    const localIds = (p.predictors && p.localPlayerIds) ? p.localPlayerIds.filter((id) => p.predictors![id]) : [];
    const predicting = localIds.length > 0;
    const pickupPredictor = predicting
      ? {
          isPickedUp: (id: any) => localIds.some((pid) => p.predictors![pid].isPickedUp(id)),
          // Earliest game-time any of our worms predicted grabbing it (or undefined).
          pickupTime: (id: any) => {
            let best = Infinity;
            for (const pid of localIds) {
              const t = p.predictors![pid].pickupTime ? p.predictors![pid].pickupTime(id) : undefined;
              if (typeof t === "number") best = Math.min(best, t);
            }
            return best === Infinity ? undefined : best;
          },
        }
      : undefined;
    // Per-opponent render time (online adaptive delay). Returns undefined when the
    // interpolator has no clock for that worm yet, so the renderers fall back to the
    // shared frame render time. Set per frame in update() via canvasState.
    const getWormRenderTime = (playerId: any) =>
      canvasState.resolveWormTime ? canvasState.resolveWormTime(playerId) : undefined;
    renderers.push(
      MapRenderer({
        gameState: p.gameState,
        canvas: refs.mapCanvas,
        borderCanvas: refs.borderCanvas,
        centerText: cfg.centerText,
        scale,
        // Wall-hack's wall effect shows for the worms this client controls (online: your
        // own; offline undefined = any worm, which suits hotseat).
        localPlayerIds: p.localPlayerIds,
      })
    );
    renderers.push(
      PowerUpRenderer({
        gameState: p.gameState,
        canvas: refs.powerUpCanvas,
        // its own: the layer may be denser than the rest (resizeCanvases)
        scale: refs.powerUpCanvas.width / p.gameState.map.width,
        predictor: pickupPredictor,
        getSpawnTime: p.powerUpTruthTime,
        getWormRenderTime,
        // Hosts the red-missile state (per view, survives renderer rebuilds mid-flight).
        canvasState,
        // For an own PREDICTED red grab (server grabbedBy not yet known): the missile
        // targeting must exclude every local player's worms, like core does.
        localPlayerIds: p.localPlayerIds,
        rotated,
      })
    );
    renderers.push(
      WormBodyRenderer({
        gameState: p.gameState,
        players: p.players,
        mainCanvas: refs.wormBodyCanvas2,
        secondaryCanvas: refs.wormBodyCanvas3,
        canvasState,
        scale,
        introDelay: cfg.introDelay,
        // Online: this one shared renderer draws EVERY worm's whole trail, your own included -
        // wall-hack ghost tracks too. The own track's per-worm clock (and its "#" ghost
        // tracks', see update()) resolves to the predicted target time, so the tail pass
        // reaches the predicted head with no separate lead or ghost draw anywhere else.
        // The predicted renderer only adds your own head + FX on top.
        getWormRenderTime,
      })
    );
    // Online play with prediction: suppress the local worms' lagged heads here and
    // let the online-only predicted renderer (below) draw them in real time instead.
    // Heads share the body renderer's per-frame canvas (tails first, heads after in
    // this same pipeline order - one less composited fullscreen layer). That body
    // renderer owns the canvas's per-frame clear; every later renderer on it skips
    // frames whose clock didn't advance, so a paused game never accumulates overdraw.
    renderers.push(
      WormHeadRenderer({
        gameState: p.gameState,
        canvasState,
        players: p.players,
        canvas: refs.wormBodyCanvas3,
        scale,
        drawTrajectories: false,
        excludeHeadPlayerIds: predicting ? localIds : undefined,
        getWormRenderTime,
        // Scope the "find your worm" pulse to worms this client controls (online:
        // your own only, not opponents). Undefined offline = every human.
        localPlayerIds: p.localPlayerIds,
        // Decorative lobby preview: draw power-up rings smaller (the readable-minimum
        // sizing reads as oversized in a zoomed-in preview tile).
        preview: cfg.preview,
        // Only watching (a replay): no worm is "yours", so none gets those effects.
        spectator: !!cfg.spectator,
        // Round-start roll call: when each player's head fades in (see round-intro.ts).
        introDelay: cfg.introDelay,
        rotated,
      })
    );
    // Online only: draw each predicted local worm's head + FX after the shared head
    // renderer (same shared canvas, so later = on top). Never added in offline play,
    // so the shared renderers above stay entirely free of prediction logic.
    // One predicted renderer PER worm-track we own (a player can control two after a
    // twin). tracks() is empty before the first predict(), so fall back to a single
    // renderer that resolves the primary track itself; update() rebuilds once the real
    // track list is known (and again if a twin appears mid-round).
    localIds.forEach((id) => {
      const predictor = p.predictors![id];
      const tracks = predictor.tracks ? predictor.tracks() : [];
      const entries = tracks.length ? tracks : [{ trackId: null }];
      entries.forEach((t: any) => {
        renderers.push(
          PredictedWormRenderer({
            gameState: p.gameState,
            players: p.players,
            localPlayerId: id,
            predictor,
            getTargetTime: p.getTargetTime,
            canvas: refs.wormBodyCanvas3,
            canvasState,
            // With a single owned worm, leave the target null so the renderer follows the predictor's
            // CURRENT primary track every frame. At a switcharoonie the swap re-points that primary
            // immediately, so the new worm's head is drawn the same frame - no 1-2 frame blank waiting
            // for update() to rebuild renderers. Only pin a specific track when we own two (a twin),
            // where each renderer must stay on its own worm.
            targetTrackId: entries.length > 1 ? t.trackId : null,
            scale,
            debug: p.debugPredicted,
            introDelay: cfg.introDelay,
            rotated,
          })
        );
      });
    });
    // Debug: server-truth ghost rings on top of the heads (online + ?ghost=1 only).
    if (p.serverGhost) {
      renderers.push(
        ServerGhostRenderer({
          gameState: p.gameState,
          players: p.players,
          getSegment: p.serverGhost.getSegment,
          getTime: p.serverGhost.getTime,
          canvas: refs.wormBodyCanvas3,
          canvasState,
          scale,
        })
      );
    }
    // Switcharoonie swap animation, drawn LAST so its portals/fireworks sit on top of the frozen
    // heads. Inert unless gameState.switchAnim is set (during a freeze->go hold). Owner-agnostic,
    // so it handles any worm-count combination without extra wiring.
    // The round winner's spotlight through the victory lap, over everything above.
    canvasState.heads = {};
    renderers.push(SpotlightRenderer({ gameState: p.gameState, canvas: refs.spotlightCanvas, canvasState }));
    renderers.push(
      SwitchAnimRenderer({
        gameState: p.gameState,
        players: p.players,
        canvas: refs.wormBodyCanvas3,
        canvasState,
        scale,
      })
    );
  }

  // One frame, guarded. The loop re-arms itself at the end of each frame, so an
  // exception anywhere in a frame (a predictor step, a renderer) used to end it for
  // good: the arena froze while sounds and scores carried on, and online the own
  // worm, no longer predicted or streamed, stood still on the host. A bad frame is
  // now dropped and the next one runs; each distinct error is logged once.
  const loopErrorsRef = useRef<Set<string>>(new Set());
  function update() {
    try {
      renderFrame();
    } catch (e: any) {
      const key = String((e && e.message) || e);
      if (!loopErrorsRef.current.has(key)) {
        loopErrorsRef.current.add(key);
        console.error("[game-canvas] frame dropped:", e);
      }
    }
    canvasStateRef.current.requestId = requestFrame(update);
  }

  function renderFrame() {
    const p = propsRef.current;
    // The renderers also hold the roster they were built with (who is who, in which
    // colour and head). A roster that changes without a new gameState (a colour
    // change, a seat handed over, the local ids arriving) left them drawing the old
    // one while the name tags showed the new: your worm in another player's colour,
    // heads missing until the next rebuild. Repaint from scratch when it changes.
    const roster = rosterRef.current;
    if (p.players !== roster.players || p.localPlayerIds !== roster.localIds) {
      roster.players = p.players;
      roster.localIds = p.localPlayerIds;
      const sig = rosterSig(p.players, p.localPlayerIds);
      if (sig !== roster.sig) {
        const first = roster.sig === null;
        roster.sig = sig;
        if (!first) {
          clear();
          setupRenderers();
        }
      }
    }
    let renderTime = p.renderTime;
    if (typeof renderTime === "function") {
      renderTime = renderTime();
    } else if (renderTime === undefined) {
      renderTime = p.gameState.gameTime;
    }
    const canvasState = canvasStateRef.current;
    // Settle each local predictor BEFORE any renderer reads gameState. predict() is what applies a
    // switcharoonie's ownership swap (and extends the trail); running it up front means the shared
    // head/body renderers see the POST-swap owners this frame instead of stale ones. Without this the
    // head renderer (which runs before the predicted renderer) would use last frame's ownership on the
    // swap frame and blank a worm's head for a frame or two. predict() is idempotent for a given
    // targetTime, so the predicted renderer's own predict() call below is then a no-op.
    let ownTargetTime: number | undefined;
    if (p.predictors && p.localPlayerIds && typeof p.getTargetTime === "function") {
      ownTargetTime = p.getTargetTime();
      for (const id of p.localPlayerIds) {
        const pr = p.predictors[id];
        if (pr && pr.predict) pr.predict(p.gameState, ownTargetTime);
      }
    }
    // Per-worm render clocks, read by the shared body/head renderers through
    // canvasState.resolveWormTime. Two layers:
    //  - adaptive delay (online): each OPPONENT renders on its own latency-sized clock;
    //  - own worms (online prediction): ALWAYS render at the predicted target time, so
    //    the one shared body renderer draws the own tail straight to the predicted head.
    //    The own worm isn't a special draw path: it's just a worm whose clock is "now"
    //    instead of "now minus delay" (its track simply never has undrawn segments ahead).
    // Undefined resolver (offline / no adapt, no prediction) falls back to the single
    // shared frame render time, unchanged.
    let resolveWormTime: ((trackId: any) => number | undefined) | undefined;
    if (p.adaptiveDelay) {
      const interp = interpRef.current!;
      if ((renderTime as number) < canvasState.prevRenderTime) interp.reset();
      const serverNowFn = p.serverTime || p.powerUpTruthTime;
      const serverNow = serverNowFn ? serverNowFn() : (renderTime as number);
      interp.tick(p.gameState, serverNow, performance.now(), p.adaptMarginSec ?? 0.03);
      resolveWormTime = (trackId: any) => interp.get(trackId, renderTime as number);
      if (p.interpStatsRef) p.interpStatsRef.current = interp.getStats;
    }
    if (ownTargetTime !== undefined) {
      // Built AFTER predict() ran, so the owned-track set is this frame's (post-swap, post-twin).
      const ownTracks = new Set<string>();
      for (const id of p.localPlayerIds!) {
        const pr = p.predictors![id];
        if (pr && pr.tracks) for (const t of pr.tracks()) ownTracks.add(t.trackId);
      }
      const base = resolveWormTime;
      const tt = ownTargetTime;
      // An owned worm's wall-hack ghost tracks ("<track>#N") are its own trail too: they
      // must render on the same predicted clock, or the mirror stub would lag the head.
      const isOwnTrack = (trackId: any) => {
        if (ownTracks.has(trackId)) return true;
        const h = typeof trackId === "string" ? trackId.indexOf("#") : -1;
        return h !== -1 && ownTracks.has(trackId.slice(0, h));
      };
      resolveWormTime = (trackId: any) => (isOwnTrack(trackId) ? tt : base ? base(trackId) : undefined);
    }
    canvasState.resolveWormTime = resolveWormTime;
    canvasState.renderers.forEach((renderer: any) => renderer.render(renderTime));
    // Dynamic-layer dirty rects: what the renderers drew this frame becomes what the
    // body renderer erases next frame (see its render()). `full` means something
    // unbounded was drawn (arrows, pulses, victory/switch FX), forcing one full clear.
    if (canvasState.dyn) {
      canvasState.dyn.prev = canvasState.dyn.full ? { full: true, rects: [] } : { full: false, rects: canvasState.dyn.cur };
    }
    // The set of worm-tracks we control can change mid-round WITHOUT a React re-render
    // (which a live round never triggers): a twin power-up adds a track, and a
    // switcharoonie hands us a DIFFERENT track (same count, new identity). Each predicted
    // renderer is bound to a fixed targetTrackId, so we must rebuild them whenever that set
    // changes in count OR identity - else after a switch the predicted renderer keeps drawing
    // our OLD worm (in our colour) and our new worm goes undrawn. Detect via a signature of
    // the owned trackIds (built here, after render() ran predict() and updated the slots).
    const preds = propsRef.current.predictors;
    const lids = preds && propsRef.current.localPlayerIds ? propsRef.current.localPlayerIds.filter((id) => preds[id]) : [];
    let trackSig = "";
    for (const id of lids) {
      const pr = preds![id];
      const tracks = pr.tracks ? pr.tracks() : [];
      for (const t of tracks) trackSig += id + ":" + t.trackId + ";";
    }
    if (trackSig !== canvasState.localTrackSig) {
      canvasState.localTrackSig = trackSig;
      setupRenderers();
    }
    canvasState.prevRenderTime = renderTime;
  }

  function clear() {
    // NOTE: the original passed `this.state` here (shape {canvasState}), so
    // `.pathSegmentRenderData` was undefined and this was a no-op. Preserved as-is
    // (clearing the canvases below is what actually matters); see migration notes.
    csf.clearPathSegmentRenderData({ canvasState: canvasStateRef.current } as any);
    // Everything was wiped: the dynamic layer's dirty-rect history is void, and the
    // next frame must rebuild from a full clear.
    canvasStateRef.current.dyn = undefined;
    forEach(canvasRefs.current, (canvas: HTMLCanvasElement) => {
      canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
    });
  }

  // Fully rebuild every layer from scratch: resize the backing stores, wipe them,
  // and recreate the renderers (which resets their per-worm draw progress so the
  // whole map, border and existing trails are repainted on the next frames, not
  // just new segments). Used on a size change AND to recover after the GPU drops a
  // canvas's contents (see the context-loss effect below).
  function repaintAll() {
    if (!mountedRef.current || !canvasRefs.current.mapCanvas) return;
    resizeCanvases();
    clear();
    setupRenderers();
  }

  // Mount: start the pipeline. After that, wipe + rebuild ONLY when the game itself
  // changes (a new gameState object = a new round/replay), NOT on every parent
  // re-render. This effect used to have no dependency array (a leftover of the
  // componentDidUpdate migration), so ANY re-render of the parent - online that's
  // every game_over / rating_update / lobby_update / chat message, and the 250ms
  // "next round in N" countdown tick - cleared every canvas and re-baked every
  // worm's full trail. With long trails that's tens of ms per wipe, 4x per second
  // through the whole between-rounds window: sustained, visible freeze-lag. All
  // legitimate rebuild triggers are covered elsewhere: a size change via the
  // ResizeObserver below (repaintAll), an owned-track change via update()'s
  // trackSig check, and a new round right here via the gameState identity.
  //
  // A LAYOUT effect, and it draws the first frame itself: the render that hands a
  // new round to this canvas is also the one that takes the round-over fade off
  // the layers (contentFade), so with a plain effect the browser painted that
  // render first, with the OLD round's trails back at full opacity and (on a
  // fresh mount) an unpainted board, and only then cleared and drew: one blink
  // right at the switch. Now the wipe + the new round's first frame land before
  // that paint, so the board goes straight from faded-out to the new round.
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      resizeCanvases();
      setupRenderers();
    } else {
      clear();
      setupRenderers();
      // update() schedules the next frame itself; drop the one already pending so
      // the loop doesn't run twice.
      window.cancelAnimationFrame(canvasStateRef.current.requestId);
    }
    update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gameState]);

  // Turning the view changes every layer's backing-store shape: rebuild them all.
  // (The container's ResizeObserver usually catches it too; this covers a turn
  // that happens to keep the same box size.)
  const firstRotationRef = useRef(true);
  useEffect(() => {
    if (firstRotationRef.current) {
      firstRotationRef.current = false;
      return;
    }
    const raf = requestAnimationFrame(repaintAll);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotated]);

  // Stop the render loop on unmount; drop the stats handle so a netstats overlay
  // never reads a dead interpolator's clocks against a new round's server time.
  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(canvasStateRef.current.requestId);
      if (propsRef.current.interpStatsRef) propsRef.current.interpStatsRef.current = null;
    };
  }, []);

  // Re-size the canvases whenever the container actually changes size (and once
  // it first becomes non-zero). Canvas backing-store size is set from offsetWidth
  // only at mount otherwise, so a 0x0 read (layout not settled) or a width/layout
  // switch (e.g. resizing across the compact breakpoint) would leave the game
  // stuck invisible. Re-setup the renderers since the scale changes with size.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    let lastW = 0;
    let lastH = 0;
    const ro = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0 || (w === lastW && h === lastH)) return;
      lastW = w;
      lastH = h;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(repaintAll);
    });
    ro.observe(container);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recover from the GPU dropping a canvas's backing store. On mobile (seen on
  // Android Chrome) the browser can evict a 2D canvas under memory pressure: it
  // fires "contextlost", and per the spec the context is only ever restored if a
  // listener calls preventDefault() on that event. With no handler at all the
  // canvas stays blank (white) until a full page reload - the game keeps
  // simulating (input still steers the worm) but nothing renders. We opt into
  // restoration and, once "contextrestored" fires, fully repaint (the border and
  // worm trails are drawn incrementally, so they won't redraw themselves).
  useEffect(() => {
    const canvases = Object.values(canvasRefs.current);
    if (canvases.length === 0) return;
    let raf = 0;
    // The layers share one GPU context, so an eviction fires "contextlost" on
    // each of them. Dedupe to one logged loss/recovery per actual eviction cycle.
    let lost = false;
    const scheduleRepaint = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(repaintAll);
    };
    const onLost = (e: Event) => {
      e.preventDefault();
      if (!lost) {
        lost = true;
        recordContextLost();
      }
    };
    const onRestored = () => {
      if (lost) {
        lost = false;
        recordContextRestored();
      }
      scheduleRepaint();
    };
    // Some Android browsers silently discard canvas contents when the tab is
    // backgrounded WITHOUT firing contextlost; repaint when we return to it.
    const onVisible = () => {
      if (document.visibilityState === "visible") scheduleRepaint();
    };
    canvases.forEach((c) => {
      c.addEventListener("contextlost", onLost);
      c.addEventListener("contextrestored", onRestored);
    });
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelAnimationFrame(raf);
      canvases.forEach((c) => {
        c.removeEventListener("contextlost", onLost);
        c.removeEventListener("contextrestored", onRestored);
      });
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let containerStyle: React.CSSProperties;
  let containerClass = "canvas-container";
  if (contentFade) {
    containerClass += " canvas-content-fade" + (contentFade.paused ? " canvas-content-fade-paused" : "");
  }
  if (config.fullscreen) {
    containerStyle = { width: "100%", height: "100%" };
    containerClass += " canvas-container-fullscreen";
  } else {
    containerStyle = {
      width: config.width || config.size || "100%",
      height: config.height || config.size || "100%",
    };
  }
  if (rotated) containerClass += " canvas-container-rotated";

  if (contentFade) {
    containerStyle = {
      ...containerStyle,
      "--content-fade-at": `${contentFade.at}s`,
      "--content-fade-dur": `${contentFade.duration}s`,
    } as React.CSSProperties;
  }

  return (
    <div ref={containerRef} className={containerClass} style={containerStyle}>
      <canvas ref={setCanvasRef("mapCanvas")} className="map-canvas-z-index" />
      <canvas ref={setCanvasRef("powerUpCanvas")} className="power-up-canvas-z-index" />
      <canvas ref={setCanvasRef("wormBodyCanvas2")} className="worm-body-canvas-2-z-index" />
      <canvas ref={setCanvasRef("wormBodyCanvas3")} className="worm-body-canvas-3-z-index" />
      <canvas ref={setCanvasRef("spotlightCanvas")} className="spotlight-canvas-z-index" />
      <canvas ref={setCanvasRef("borderCanvas")} className="border-canvas-z-index" />
      {children}
    </div>
  );
}
